import React, { createContext, useContext, useEffect, useState } from 'react';

// Default scoring weights and parameters
export const DEFAULT_SCORING_PARAMS = {
  weights: {
    success: 30,        // 30% - Most important: did it work without errors?
    speed: 10,          // 10% - Response time matters but not critical
    completeness: 20,   // 20% - Fully addresses the prompt
    accuracy: 20,       // 20% - Factually correct and on-topic
    readability: 5,     // 5% - Easy to read and understand
    structure: 10,      // 10% - Well-formatted with proper organization
    depth: 5            // 5% - Level of detail and thoroughness
  },
  thresholds: {
    maxResponseTime: 30,      // Maximum acceptable response time in seconds
    minTokens: 10,            // Minimum tokens for a valid response
    maxTokens: 4000,          // Maximum tokens before penalty
    minWords: 5,              // Minimum words for completeness
    targetWords: 500,         // Ideal word count (flexible)
    maxWords: 2000            // Maximum words before verbosity penalty
  }
};

const DEFAULT_SETTINGS = {
  gpt: { personalityId: 'default', model: 'gpt-4o-mini' },
  claude: { personalityId: 'default', model: 'claude-opus-4-20250514' },
  gemini: { personalityId: 'default', model: 'gemini-2.5-flash' },
  scoring: DEFAULT_SCORING_PARAMS,
  tournamentMode: 'manual', // 'manual' or 'auto'
  viewMode: 'tournament', // 'tournament' or 'solo'
  soloAgent: 'gpt' // Default agent for solo mode: 'gpt' | 'claude' | 'gemini'
};

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Load from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('neuroforge-settings');
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        // Clean up old showScoringExplanation if it exists
        const { showScoringExplanation, ...cleanSettings } = parsedSettings;
        // Merge with defaults to ensure all new fields are present
        setSettings({
          ...DEFAULT_SETTINGS,
          ...cleanSettings,
          scoring: { ...DEFAULT_SCORING_PARAMS, ...cleanSettings.scoring }
        });
      } catch (e) {
        console.warn('Invalid settings in storage:', e);
      }
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('neuroforge-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = (agent, key, value) => {
    setSettings(prev => ({
      ...prev,
      [agent]: {
        ...prev[agent],
        [key]: value
      }
    }));
  };

  const updateScoringWeights = (weights) => {
    setSettings(prev => ({
      ...prev,
      scoring: {
        ...prev.scoring,
        weights: { ...prev.scoring.weights, ...weights }
      }
    }));
  };

  const updateScoringThresholds = (thresholds) => {
    setSettings(prev => ({
      ...prev,
      scoring: {
        ...prev.scoring,
        thresholds: { ...prev.scoring.thresholds, ...thresholds }
      }
    }));
  };

  const resetScoringToDefaults = () => {
    setSettings(prev => ({
      ...prev,
      scoring: DEFAULT_SCORING_PARAMS
    }));
  };

  const setTournamentMode = (mode) => {
    setSettings(prev => ({
      ...prev,
      tournamentMode: mode
    }));
  };

  const setViewMode = (mode) => {
    setSettings(prev => ({
      ...prev,
      viewMode: mode
    }));
  };

  const setSoloAgent = (agent) => {
    setSettings(prev => ({
      ...prev,
      soloAgent: agent
    }));
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSetting,
      updateScoringWeights,
      updateScoringThresholds,
      resetScoringToDefaults,
      setTournamentMode,
      setViewMode,
      setSoloAgent
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
